import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  FolderKanban,
  Plus,
  Search,
  MoreVertical,
  Pencil,
  Trash2,
  Calendar,
} from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { useSortableTable } from "../hooks/useSortableTable";
import { SortableTableHead } from "../components/SortableTableHead";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PHASE_OPTIONS = ["Quote", "Planning", "In Progress", "Quality Check", "Complete"];

const Projects = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("wip"); // "wip" or "completed"
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const { sortKey, sortDir, toggleSort, sortData } = useSortableTable();
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    current_phase: "Quote",
    due_date: "",
    start_date: "",
    end_date: "",
  });

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`, {
        withCredentials: true,
      });
      setProjects(response.data);
    } catch (error) {
      console.error("Error fetching projects:", error);
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProject) {
        await axios.put(`${API}/projects/${editingProject.project_id}`, formData, {
          withCredentials: true,
        });
        toast.success("Project updated successfully");
      } else {
        await axios.post(`${API}/projects`, formData, {
          withCredentials: true,
        });
        toast.success("Project created successfully");
      }
      setSheetOpen(false);
      resetForm();
      fetchProjects();
    } catch (error) {
      console.error("Error saving project:", error);
      toast.error("Failed to save project");
    }
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      name: project.name,
      description: project.description || "",
      current_phase: project.current_phase || "Quote",
      due_date: project.due_date || "",
      start_date: project.start_date || "",
      end_date: project.end_date || "",
    });
    setSheetOpen(true);
  };

  const handleDelete = async () => {
    if (!projectToDelete) return;
    try {
      await axios.delete(`${API}/projects/${projectToDelete.project_id}`, {
        withCredentials: true,
      });
      toast.success("Project deleted successfully");
      setDeleteDialogOpen(false);
      setProjectToDelete(null);
      fetchProjects();
    } catch (error) {
      console.error("Error deleting project:", error);
      toast.error("Failed to delete project");
    }
  };

  const resetForm = () => {
    setEditingProject(null);
    setFormData({
      name: "",
      description: "",
      current_phase: "Quote",
      due_date: "",
      start_date: "",
      end_date: "",
    });
  };

  const getPhaseColor = (phase) => {
    switch (phase) {
      case "Complete":
        return "bg-emerald-50 text-emerald-700";
      case "In Progress":
        return "bg-blue-50 text-blue-700";
      case "Quality Check":
        return "bg-purple-50 text-purple-700";
      case "Planning":
        return "bg-amber-50 text-amber-700";
      case "Quote":
        return "bg-slate-100 text-slate-700";
      default:
        return "bg-slate-50 text-slate-600";
    }
  };

  const filteredProjects = projects
    .filter((project) => {
      const matchesSearch =
        project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (project.description &&
          project.description.toLowerCase().includes(searchQuery.toLowerCase()));
      const isComplete = project.current_phase === "Complete";
      const matchesView = viewMode === "completed" ? isComplete : !isComplete;
      return matchesSearch && matchesView;
    })
    .sort((a, b) => {
      if (viewMode === "completed") {
        // Sort completed by due_date ascending
        const dateA = a.due_date || "";
        const dateB = b.due_date || "";
        return dateA.localeCompare(dateB);
      }
      // WIP: sort by updated_at descending (most recent first)
      const dateA = a.updated_at || a.created_at || "";
      const dateB = b.updated_at || b.created_at || "";
      return dateB.localeCompare(dateA);
    });

  // User-triggered column sort overrides the default view-based sort
  const sortedProjects = sortData(filteredProjects, {
    project: (p) => p.name,
    phase: (p) => p.current_phase,
    due_date: (p) => p.due_date,
    start_date: (p) => p.start_date,
    end_date: (p) => p.end_date,
  });

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-48"></div>
          <div className="h-64 bg-slate-200 rounded-sm"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="projects-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">
            Projects
          </h1>
          <p className="text-slate-500 mt-1">
            Manage your manufacturing projects
          </p>
        </div>
        <Sheet open={sheetOpen} onOpenChange={(open) => {
          setSheetOpen(open);
          if (!open) resetForm();
        }}>
          <SheetTrigger asChild>
            <Button
              className="rounded-sm bg-slate-900 hover:bg-slate-800"
              data-testid="create-project-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Project
            </Button>
          </SheetTrigger>
          <SheetContent className="sm:max-w-md">
            <SheetHeader>
              <SheetTitle>
                {editingProject ? "Edit Project" : "Create Project"}
              </SheetTitle>
              <SheetDescription>
                {editingProject
                  ? "Update project details"
                  : "Add a new manufacturing project"}
              </SheetDescription>
            </SheetHeader>
            <form onSubmit={handleSubmit} className="mt-6 space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name">Project Name *</Label>
                <Input
                  id="name"
                  data-testid="project-name-input"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="Enter project name"
                  required
                  className="rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  data-testid="project-description-input"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Enter project description"
                  rows={3}
                  className="rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phase">Phase</Label>
                <Select
                  value={formData.current_phase}
                  onValueChange={(value) =>
                    setFormData({ ...formData, current_phase: value })
                  }
                >
                  <SelectTrigger className="rounded-sm" data-testid="project-phase-select">
                    <SelectValue placeholder="Select phase" />
                  </SelectTrigger>
                  <SelectContent>
                    {PHASE_OPTIONS.map((phase) => (
                      <SelectItem key={phase} value={phase}>
                        {phase}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="due_date">Due Date</Label>
                <Input
                  id="due_date"
                  type="date"
                  data-testid="project-due-date"
                  value={formData.due_date}
                  onChange={(e) =>
                    setFormData({ ...formData, due_date: e.target.value })
                  }
                  className="rounded-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start_date">Start Date</Label>
                  <Input
                    id="start_date"
                    type="date"
                    data-testid="project-start-date"
                    value={formData.start_date}
                    onChange={(e) =>
                      setFormData({ ...formData, start_date: e.target.value })
                    }
                    className="rounded-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end_date">End Date</Label>
                  <Input
                    id="end_date"
                    type="date"
                    data-testid="project-end-date"
                    value={formData.end_date}
                    onChange={(e) =>
                      setFormData({ ...formData, end_date: e.target.value })
                    }
                    className="rounded-sm"
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setSheetOpen(false);
                    resetForm();
                  }}
                  className="flex-1 rounded-sm"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 rounded-sm bg-slate-900 hover:bg-slate-800"
                  data-testid="save-project-btn"
                >
                  {editingProject ? "Update" : "Create"}
                </Button>
              </div>
            </form>
          </SheetContent>
        </Sheet>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search projects..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 rounded-sm"
            data-testid="search-projects"
          />
        </div>
        <div className="flex gap-2">
          <Button
            variant={viewMode === "wip" ? "default" : "outline"}
            onClick={() => setViewMode("wip")}
            className="rounded-sm"
            data-testid="filter-wip"
          >
            Work In Progress
          </Button>
          <Button
            variant={viewMode === "completed" ? "default" : "outline"}
            onClick={() => setViewMode("completed")}
            className="rounded-sm"
            data-testid="filter-completed"
          >
            Completed
          </Button>
        </div>
      </div>

      {/* Projects List */}
      <Card className="rounded-sm border-slate-200">
        <CardContent className="p-0">
          {sortedProjects.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHead columnKey="project" sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort}>Project</SortableTableHead>
                  <SortableTableHead columnKey="phase" sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort}>Phase</SortableTableHead>
                  <SortableTableHead columnKey="due_date" sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort}>Due Date</SortableTableHead>
                  <SortableTableHead columnKey="start_date" sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort}>Start</SortableTableHead>
                  <SortableTableHead columnKey="end_date" sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort}>End</SortableTableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedProjects.map((project) => (
                  <TableRow
                    key={project.project_id}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => navigate(`/projects/${project.project_id}`)}
                    data-testid={`project-row-${project.project_id}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-slate-100 rounded-sm flex items-center justify-center">
                          <FolderKanban className="h-4 w-4 text-slate-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{project.name}</p>
                          {project.description && (
                            <p className="text-xs text-slate-400 line-clamp-1 max-w-xs">
                              {project.description}
                            </p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={`rounded-sm text-xs ${getPhaseColor(project.current_phase)}`}>
                        {project.current_phase || "No Phase"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {project.due_date ? (
                        <div className="flex items-center gap-1.5 text-sm text-slate-600">
                          <Calendar className="h-3.5 w-3.5" />
                          {project.due_date}
                        </div>
                      ) : (
                        <span className="text-slate-400 text-sm">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-slate-500">
                        {project.start_date || "—"}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-slate-500">
                        {project.end_date || "—"}
                      </span>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit(project)}>
                            <Pencil className="mr-2 h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => {
                              setProjectToDelete(project);
                              setDeleteDialogOpen(true);
                            }}
                            className="text-red-600 focus:text-red-600"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-16">
              <FolderKanban className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                {viewMode === "completed" ? "No completed projects" : "No projects in progress"}
              </h3>
              <p className="text-slate-500 mb-6">
                {searchQuery
                  ? "Try adjusting your search"
                  : viewMode === "completed"
                    ? "Completed projects will appear here"
                    : "Get started by creating your first project"}
              </p>
              {!searchQuery && viewMode === "wip" && (
                <Button
                  onClick={() => setSheetOpen(true)}
                  className="rounded-sm bg-slate-900 hover:bg-slate-800"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Project
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Project</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{projectToDelete?.name}"? This
              action cannot be undone and will remove all associated phases and data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="rounded-sm bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Projects;
